import { memo, useCallback, useMemo } from "react";
import { ErrorPageContent } from "@ploy-ui/core";
import { SectionProps } from "../Section";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import { useDispatch, useVariableData } from "@ploy-lib/calculation";
import { ErrorHandler } from "@ploy-lib/core";
import ClearIcon from "@material-ui/icons/Clear";
import {
	Grid,
	IconButton,
	lighten,
	makeStyles,
	Paper,
	Typography
} from "@material-ui/core";
import { defineMessages } from "react-intl";
import clsx from "clsx";
import { useIntl } from "react-intl";
import { Property, propertyMessage } from "@ploy-lib/types";

addRegisteredSectionLayout({
	name: "SimplifiedPropertiesSection",
	displayName: "SimplifiedPropertiesSection",
	settings: {
		editableOptions: {}
	}
});

const useStyles = makeStyles(
	theme => ({
		root: {},
		cardBorder: {
			borderColor: theme.palette.secondary.main
		},
		selectedCard: {
			backgroundColor: lighten(theme.palette.secondary.main, 0.9)
		},
		simpleSpacing: {
			padding: theme.spacing(1)
		},
		centerButton: {
			textAlign: "center"
		},
		removeButton: {},
		listTitle: {}
	}),
	{ name: "DploySimplifiedPropertiesSection" }
);

const messages = defineMessages({
	weFoundPropertySingularTitle: {
		id: "template-form.property.weFoundPropertySingularTitle",
		defaultMessage: "We found this property:"
	},
	weFoundPropertyPluralTitle: {
		id: "template-form.property.weFoundPropertyPluralTitle",
		defaultMessage: "We found these properties:"
	},
	removeProperty: {
		id: "template-form.property.removeProperty",
		defaultMessage: "Remove property"
	}
});

/* Pad postal codes less than 4 digits (because the leading 0s are stripped)
  	e.g.: "375" => "0375" */
const padPostalCode = (postalCode: string | undefined): string => {
	if (!postalCode) return "";
	if (postalCode.length === 4) return postalCode;

	return postalCode.padStart(4, "0");
};

const SingleProperty = (props: {
	property: Property;
	onRemove: (p: Property) => void;
}) => {
	const styles = useStyles();
	const intl = useIntl();

	const { property, onRemove } = props;

	return (
		<Paper
			variant="outlined"
			className={clsx(styles.cardBorder, {
				[styles.selectedCard]: property.refinance
			})}
		>
			<Grid
				container
				alignItems="center"
				alignContent="center"
				justifyContent="center"
				className={styles.simpleSpacing}
			>
				<Grid item xs={10} className={styles.simpleSpacing}>
					<Grid
						container
						direction="column"
						justifyContent="space-between"
						spacing={1}
					>
						<Grid item>
							<Typography variant="h5">
								{intl.formatMessage(propertyMessage[property.type])}
							</Typography>
							{property.type !== "Project" ? (
								<Typography variant="body1">
									{property.type === "Cadastre"
										? property.cadastralNumber.replaceAll(/-/g, "/")
										: null}
									{property.type === "Share" ? (
										<>
											{property.housingCooperativeName}
											<br />
											{property.housingCooperativeOrgNumber}
											<br />
											{property.shareNumber}
										</>
									) : null}
									{property.type === "Stock" ? (
										<>
											{property.housingCooperativeOrgName}
											<br />
											{property.apartmentNumber}
											<br />
											{property.businessManager}
										</>
									) : null}
								</Typography>
							) : null}
						</Grid>

						<Grid item>
							{property.streetName ||
							property.addressNumber ||
							property.addressLetter ||
							property.postalCode ||
							property.postalPlace ? (
								<Typography variant="body1">
									{property.streetName} {property.addressNumber}{" "}
									{property.addressLetter}
									<br />
									{padPostalCode(property.postalCode)} {property.postalPlace}
								</Typography>
							) : null}

							{property.type === "Project" ? (
								<Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
									{property.freeText?.trim()}
								</Typography>
							) : null}
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={2} className={styles.centerButton}>
					<IconButton
						onClick={() => onRemove(property)}
						title={intl.formatMessage(messages.removeProperty)}
					>
						<ClearIcon />
					</IconButton>
				</Grid>
			</Grid>
		</Paper>
	);
};

const SimplifiedPropertiesSectionInternal = memo((props: SectionProps) => {
	const styles = useStyles(props);
	let { value: properties } = useVariableData<Property[]>(
		"Calculator",
		"PROPERTIES"
	);
	if (typeof properties === "string") properties = [];
	const dispatch = useDispatch();
	const intl = useIntl();

	const onRemoveProperty = useCallback(
		(property: Property) => {
			const newProperties = [...(properties ?? [])].filter(
				p => p.uniquePropertyId !== property.uniquePropertyId
			);
			dispatch({
				type: "patch",
				payload: {
					patches: [
						{
							target: "PROPERTIES",
							namespace: "Calculator",
							value: [...newProperties],
							overwrite: true
						}
					]
				}
			});
		},
		[dispatch, properties]
	);

	var propertyCards = useMemo(
		() =>
			properties?.map(p => {
				return (
					<Grid key={p.uniquePropertyId} item xs={12}>
						<SingleProperty property={p} onRemove={onRemoveProperty} />
					</Grid>
				);
			}),
		[onRemoveProperty, properties]
	);

	const somePropertyIsMainResidence = properties?.some(
		p => p.mainCustomerResidence
	);

	if (
		somePropertyIsMainResidence ||
		propertyCards == null ||
		propertyCards.length === 0
	) {
		return null;
	}

	return (
		<Grid
			container
			spacing={1}
			className={clsx(styles.root, props.className)}
			onClick={props.onClick}
		>
			<Typography variant="h4" className={styles.listTitle}>
				{propertyCards.length > 1
					? intl.formatMessage(messages.weFoundPropertyPluralTitle)
					: intl.formatMessage(messages.weFoundPropertySingularTitle)}
			</Typography>
			{propertyCards}
		</Grid>
	);
});

const SimplifiedPropertiesSection = (props: SectionProps) => (
	<ErrorHandler
		fallback={e => (
			<ErrorPageContent
				className={props.className}
				onClick={props.onClick}
				header={
					<span>
						{SimplifiedPropertiesSection.displayName} failed to render.
					</span>
				}
			/>
		)}
	>
		<SimplifiedPropertiesSectionInternal {...props} />
	</ErrorHandler>
);
SimplifiedPropertiesSection.displayName = "SimplifiedPropertiesSection";

export { SimplifiedPropertiesSection };
